import { h1 } from "@/components/zthd/Title";
import { cn, countSuffix } from "@/lib/utils";
import { versionLabel } from "@/lib/version";
import { useParams } from "react-router-dom";
import { useTabParam } from "@/hooks/tab-param";
import useSWR from "swr";
import { swrKey } from "@/lib/swr";
import { Tabs, TabsContent, TabsTrigger } from "@/components/ui/tabs";

import { PageBreadcrumb } from "@/components/page-breadcrumb";
import {
  DetailAside,
  DetailColumns,
  DetailMain,
} from "@/components/detail-columns";
import { Section } from "@/components/section";
import { ListEmpty, ListLoading } from "@/components/list/status-text";
import Page403 from "@/components/zthd/403";
import Page404 from "@/components/zthd/404";
import { useApp, useApps } from "@/hooks/apps";
import { StateWithAction } from "./columns";
import { VersionRowActions } from "./version-row-actions";
import {
  AddReleaseDialog,
  VersionTimelineBody,
} from "./columns/column-publish-log";
import { ReportCard } from "@/screens/Reports/report-card";
import { CreateReportDialog } from "@/screens/Apps/create-report-dialog";
import Engagements from "./Engagements";
import BugsList from "@/components/versions/bugs-list";
import ChannelReleases from "@/components/versions/channel-releases";
import ArtifactsSection from "@/components/versions/artifacts-card";
import RolloutTasksSection from "@/components/versions/rollout-tasks";
import BuildButtons from "@/components/versions/build-buttons";
import SubmitBugs from "./SubmitBugs";
import type { Prd, Report, Version } from "@/types/domain";
import { TabBar } from "@/components/nav/route-tabs";

// Reports related to this version (version + its PRD reports, all with versionId).
const VersionReports = ({ versionId }: { versionId: string }) => {
  const { data, isLoading } = useSWR<{ data: Report[]; total: number }>(
    swrKey("/reports", { versionId, pageSize: 100 }),
  );
  const reports: Report[] = data?.data ?? [];

  if (reports.length === 0) {
    return isLoading ? <ListLoading /> : <ListEmpty>No reports.</ListEmpty>;
  }
  return (
    <div className="flex flex-col gap-1">
      {reports.map((r) => (
        <ReportCard key={r.id} report={r} />
      ))}
    </div>
  );
};

const TAB_LABELS: Record<string, string> = {
  prds: "PRDs",
  reports: "Reports",
  bugs: "Bugs",
  channels: "Channels",
};

const Detail = () => {
  const { id, pid } = useParams();
  const {
    data: version,
    isLoading,
    error,
  } = useSWR<Version>([`/versions/${id}`]);
  const apps = useApps();
  const [rawTab, setTab] = useTabParam("tab", "prds");

  // Tab-label counts; keys mirror the tab contents' own fetches so SWR dedupes.
  const { data: engagements } = useSWR<unknown[]>(
    id ? swrKey("/engagements", { versionId: id }) : null,
  );
  const { data: reportsData } = useSWR<{ data: Report[]; total: number }>(
    id ? swrKey("/reports", { versionId: id, pageSize: 100 }) : null,
  );
  // Same key BugsList(versionId) fetches by (issue unification)
  const { data: bugsData } = useSWR<{ data: Prd[]; total: number }>(
    id
      ? swrKey("/prds", { type: "bug", pageSize: 100, sourceVersionId: id })
      : null,
  );
  const tabCounts: Record<string, number | undefined> = {
    prds: engagements?.length,
    reports: reportsData?.total ?? reportsData?.data?.length,
    bugs: bugsData?.total ?? bugsData?.data?.length,
  };

  // Channels tab only for apps with a channel manifest (e.g. builds shipped
  // per app store). Label shows released/total progress instead of a count.
  // Channel-less apps record releases from the release timeline instead.
  const { data: channels } = useSWR<unknown[]>(
    version?.pid ? swrKey("/channels", { pid: version.pid }) : null,
  );
  const hasChannels = (channels?.length ?? 0) > 0;
  // Release records of a channel-less app are added from the header; apps
  // with channels record per channel on the channel detail page.
  const channelless = !!channels && channels.length === 0;
  const { data: channelReleases } = useSWR<unknown[]>(
    id && hasChannels ? swrKey("/channel-releases", { versionId: id }) : null,
  );
  const tabValues = [
    "prds",
    "reports",
    "bugs",
    ...(hasChannels ? ["channels"] : []),
  ];
  // Stale/invalid ?tab= (e.g. "channels" on an app without a manifest) falls
  // back to the default instead of rendering no content.
  const tab = tabValues.includes(rawTab) ? rawTab : "prds";
  const tabSuffix = (value: string) =>
    value === "channels"
      ? channelReleases
        ? ` (${channelReleases.length}/${channels!.length})`
        : ""
      : countSuffix(tabCounts[value]);

  const versionTitle = version ? versionLabel(version, apps) : "Version detail";

  // Entered from an app (/apps/:pid/versions/:id): parent is that app's
  // versions list, not the global one — back-navigation stays in the app.
  const app = useApp(pid) ?? null;
  const parent = pid
    ? { label: app?.name ?? "App", to: `/apps/${pid}/versions` }
    : { label: "Versions", to: "/versions" };

  // Same policy as BugDetail: exists-but-invisible → 403, missing → 404, no empty shell.
  if (isLoading) return <ListLoading />;
  if (error?.status === 403) return <Page403 />;
  if (!version) return <Page404 />;

  return (
    <div className="flex flex-col">
      <PageBreadcrumb parent={parent} current={versionTitle} />

      {/* Header: version name as the main title, action buttons at top right;
          on narrow screens the action group drops to its own row under the
          title instead of squeezing it out. */}
      <div className="mb-4 flex w-full flex-wrap items-start gap-3">
        <div className="min-w-0 flex-1">
          <h1 className={cn(h1, "truncate")}>{versionTitle}</h1>
        </div>
        <div className="flex w-full shrink-0 flex-wrap items-center justify-end gap-2 md:w-auto">
          {version && <BuildButtons version={version} />}
          {version && channelless && (
            <AddReleaseDialog versionId={version.id} />
          )}
          {version && (
            <StateWithAction
              version={version}
              variant="outline"
              className="h-8 px-3"
            />
          )}
          {version && <VersionRowActions version={version} />}
        </div>
      </div>

      <DetailColumns>
        <DetailMain>
          <Tabs value={tab} onValueChange={setTab}>
            {/* Per-tab create action sits right of the tab bar. Report
                entry only once rollout started (state >= 200). */}
            <TabBar
              options={tabValues.map((value) => ({
                value,
                label: `${TAB_LABELS[value]}${tabSuffix(value)}`,
              }))}
              value={tab}
              onValueChange={setTab}
              action={
                <>
                  {tab === "reports" &&
                    version &&
                    (version.state ?? 0) >= 200 && (
                      <CreateReportDialog version={version} autoOpenParam />
                    )}
                  {tab === "bugs" && version && (
                    <SubmitBugs version={version} />
                  )}
                </>
              }
            >
              {tabValues.map((value) => (
                <TabsTrigger key={value} value={value}>
                  {TAB_LABELS[value]}
                  {tabSuffix(value)}
                </TabsTrigger>
              ))}
            </TabBar>
            <TabsContent value="prds">
              <Engagements />
            </TabsContent>
            <TabsContent value="reports">
              {id && <VersionReports versionId={id} />}
            </TabsContent>
            <TabsContent value="bugs">
              {id && <BugsList versionId={id} />}
            </TabsContent>
            {hasChannels && (
              <TabsContent value="channels">
                {id && version && (
                  <ChannelReleases versionId={id} pid={String(version.pid)} />
                )}
              </TabsContent>
            )}
          </Tabs>
        </DetailMain>
        {version && (
          <DetailAside>
            <Section title="Release timeline">
              <VersionTimelineBody version={version} />
            </Section>
            <RolloutTasksSection version={version} />
            {id && (
              <ArtifactsSection versionId={id} pid={String(version.pid)} />
            )}
          </DetailAside>
        )}
      </DetailColumns>
    </div>
  );
};

export default Detail;
